import React, { useState } from 'react';
import { styles } from './superadminStyles';

// Formats a raw token field value into the shape the backend enforces
// (GisLayerMapping.token: /^GIS_[A-Z0-9_]+$/) as the admin types — uppercase
// always, GIS_ prefix only added once they've actually typed something (so
// the field doesn't start pre-filled with "GIS_" before they type anything).
const formatTokenInput = (raw) => {
  const upper = raw.toUpperCase().replace(/[^A-Z0-9_]/g, '');
  if (!upper) return '';
  return upper.startsWith('GIS_') ? upper : `GIS_${upper}`;
};

// existingMapping.clientId may be a populated {_id, name} object (from GET's
// .populate()) or a raw ObjectId string (from the POST/PUT response) —
// normalize to a plain id string for the <select> value.
const extractClientId = (clientId) => (clientId && typeof clientId === 'object' ? clientId._id : clientId) || '';

const MappingModal = ({ existingMapping, clients, onClose, onSave }) => {
  const [token, setToken] = useState(existingMapping?.token || '');
  const [tableName, setTableName] = useState(existingMapping?.tableName || '');
  const [valueColumn, setValueColumn] = useState(existingMapping?.valueColumn || '');
  const [label, setLabel] = useState(existingMapping?.label || '');
  const [description, setDescription] = useState(existingMapping?.description || '');
  const [isActive, setIsActive] = useState(existingMapping?.isActive ?? true);
  const [clientId, setClientId] = useState(extractClientId(existingMapping?.clientId));
  const [error, setError] = useState('');

  const isEditing = !!existingMapping;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await onSave({ token, tableName, valueColumn, label, description, isActive, clientId: clientId || null }, existingMapping?._id);
      onClose();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to save GIS layer mapping.');
    }
  };

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modalBox}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, color: '#1abc9c' }}>{isEditing ? 'Edit GIS Layer Mapping' : 'Add GIS Layer Mapping'}</h2>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', color: 'white', fontSize: '1.2rem', cursor: 'pointer' }}>✖</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '15px' }}>
            <label style={styles.label}>Token</label>
            <input
              type="text"
              value={token}
              onChange={(e) => setToken(formatTokenInput(e.target.value))}
              placeholder="e.g. GIS_SOILDRAINAGE_N"
              style={{ ...styles.input, fontFamily: 'monospace', opacity: isEditing ? 0.6 : 1 }}
              disabled={isEditing}
              required
            />
            {isEditing && (
              <div style={{ fontSize: '0.75rem', color: '#bdc3c7', marginTop: '4px' }}>
                Token cannot be changed once created — formulas reference it directly.
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
            <div>
              <label style={styles.label}>PostGIS Table</label>
              <input type="text" value={tableName} onChange={(e) => setTableName(e.target.value)} style={{ ...styles.input, fontFamily: 'monospace' }} required />
            </div>
            <div>
              <label style={styles.label}>Value Column</label>
              <input type="text" value={valueColumn} onChange={(e) => setValueColumn(e.target.value)} style={{ ...styles.input, fontFamily: 'monospace' }} required />
            </div>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={styles.label}>Label</label>
            <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} style={styles.input} required />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={styles.label}>Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} style={{ ...styles.input, height: '60px', resize: 'vertical' }} />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={styles.label}>Client Scope</label>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)} style={styles.input}>
              <option value="">Global (visible to all clients)</option>
              {(clients || []).map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
            <div style={{ fontSize: '0.75rem', color: '#bdc3c7', marginTop: '4px' }}>
              Scoped mappings only appear in that client's Formula Builder token picker — other clients never see them.
            </div>
          </div>

          <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input type="checkbox" id="gis-mapping-active" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            <label htmlFor="gis-mapping-active" style={{ ...styles.label, marginBottom: 0 }}>Active</label>
          </div>

          {error && <div style={{ color: '#e74c3c', marginBottom: '15px', fontSize: '0.9rem' }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button type="button" onClick={onClose} style={{ ...styles.btn, background: 'transparent', border: '1px solid #7f8c8d' }}>
              Cancel
            </button>
            <button type="submit" style={{ ...styles.btn, background: '#1abc9c' }}>
              {isEditing ? 'Save Changes' : 'Add Mapping'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const GisLayerMappings = ({ mappings, clients, handleSaveMapping, handleDeleteMapping }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);

  const openAddModal = () => { setEditingMapping(null); setIsModalOpen(true); };
  const openEditModal = (mapping) => { setEditingMapping(mapping); setIsModalOpen(true); };
  const closeModal = () => { setIsModalOpen(false); setEditingMapping(null); };

  return (
    <div style={{ ...styles.card, gridColumn: '1 / -1' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
        <div>
          <h2 style={{ marginTop: 0, marginBottom: '4px', color: '#1abc9c' }}>🗺️ GIS Layer Mappings</h2>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#bdc3c7' }}>
            {'{GIS_xxxx}'} formula tokens the Outcome Algorithm evaluator resolves against real PostGIS tables.
          </p>
        </div>
        <button onClick={openAddModal} style={{ ...styles.btn, background: '#1abc9c', padding: '8px 14px', fontSize: '0.85rem' }}>
          ➕ Add Mapping
        </button>
      </div>

      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
          <thead>
            <tr style={{ background: '#2c3e50', color: '#bdc3c7', textAlign: 'left' }}>
              <th style={{ padding: '8px' }}>Token</th>
              <th style={{ padding: '8px' }}>PostGIS Table</th>
              <th style={{ padding: '8px' }}>Value Column</th>
              <th style={{ padding: '8px' }}>Label</th>
              <th style={{ padding: '8px' }}>Scope</th>
              <th style={{ padding: '8px' }}>Status</th>
              <th style={{ padding: '8px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(mappings || []).map(m => {
              const scopedClientId = extractClientId(m.clientId);
              const scopedClientName = (m.clientId && typeof m.clientId === 'object' && m.clientId.name)
                || (clients || []).find(c => c._id === scopedClientId)?.name;
              return (
              <tr key={m._id} style={{ borderBottom: '1px solid #7f8c8d' }}>
                <td style={{ padding: '8px', fontFamily: 'monospace', fontWeight: 'bold' }}>{m.token}</td>
                <td style={{ padding: '8px', fontFamily: 'monospace' }}>{m.tableName}</td>
                <td style={{ padding: '8px', fontFamily: 'monospace' }}>{m.valueColumn}</td>
                <td style={{ padding: '8px' }}>{m.label}</td>
                <td style={{ padding: '8px' }}>
                  <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold', background: scopedClientId ? '#8e44ad' : '#34495e' }}>
                    {scopedClientId ? (scopedClientName || 'Unknown Client') : 'GLOBAL'}
                  </span>
                </td>
                <td style={{ padding: '8px' }}>
                  <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 'bold', background: m.isActive ? '#27ae60' : '#7f8c8d' }}>
                    {m.isActive ? 'ACTIVE' : 'INACTIVE'}
                  </span>
                </td>
                <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>
                  <button onClick={() => openEditModal(m)} style={{ ...styles.btn, background: '#3498db', padding: '4px 10px', fontSize: '0.8rem', marginRight: '8px' }}>
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm(`Delete GIS layer mapping "${m.token}"? Any formula referencing it will default to a factor of 1.0.`)) {
                        handleDeleteMapping(m._id);
                      }
                    }}
                    style={{ ...styles.btn, background: '#c0392b', padding: '4px 10px', fontSize: '0.8rem' }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
              );
            })}
            {(!mappings || mappings.length === 0) && (
              <tr><td colSpan="7" style={{ padding: '20px', textAlign: 'center', color: '#bdc3c7' }}>No GIS layer mappings configured.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <MappingModal
          existingMapping={editingMapping}
          clients={clients}
          onClose={closeModal}
          onSave={handleSaveMapping}
        />
      )}
    </div>
  );
};

export default GisLayerMappings;
